/**
 * "Per project"-weergave van de renovatieticketlijst.
 *
 * Twee groeperingsniveaus: renovatieproject (open-/dichtklikbaar) en daarbinnen
 * renovatieonderdeel. Rijen, pickers, bulkacties en kolomgedrag komen uit
 * dezelfde UniversalTable als de vlakke lijst; enkel de kolomconfiguratie
 * verschilt (Project en Onderdeel als eerste inhoudelijke kolommen).
 *
 * Groeperen, sorteren en de tellers gebeuren backend-side — zie
 * `useRenoTicketGroups` en `GET /reno-helpdesk/tickets/grouped`.
 *
 * @module RenoHelpdesk/Components/RenoProjectGroupedTable
 */

import { Warning } from '@phosphor-icons/react';
import { UniversalTable } from 'shared/components/UniversalTable';
import type { BulkAction } from 'shared/components/filterBar/types';
import type { CellConfig } from 'shared/components/UniversalTable/components';
import type { PickerConfig } from 'shared/components/UniversalTable/types';
import { RENO_PROJECT_TABLE_CONFIG } from '../configs/renoTableConfig';
import {
  RenoGroupedTicketRow,
  RenoTicketPartGroup,
  RenoTicketProjectGroup,
} from '../types/renoTicket';
import { RenoPartGroupHeader, RenoProjectGroupHeader } from './RenoGroupHeaders';

interface RenoProjectGroupedTableProps {
  rows: RenoGroupedTicketRow[];
  /** Projectmetadata per groepeersleutel (voor de tellers in de koppen). */
  groupsByKey: Map<string, RenoTicketProjectGroup>;
  /** Onderdeelmetadata per groepeersleutel. */
  partsByKey: Map<string, { group: RenoTicketProjectGroup; part: RenoTicketPartGroup }>;
  isLoading: boolean;
  selectedRows: Set<number>;
  onSelectionChange: (rows: Set<number>) => void;
  renderRow: (ticket: any, context: any) => CellConfig[];
  bulkActions: BulkAction[];
  onUpdate: () => void;
  /** true wanneer de ticketlimiet bereikt is en de gebruiker moet filteren. */
  truncated: boolean;
  loaded: number;
  total: number;
  /**
   * Picker-configuratie; standaard die van de per-project weergave. De lijstpagina
   * geeft een variant mee waarin de tagcatalogus gevuld is (die is pas op runtime
   * bekend), zodat beide weergaven exact dezelfde pickers hebben.
   */
  pickerConfig?: PickerConfig;
}

export const RenoProjectGroupedTable = ({
  rows,
  groupsByKey,
  partsByKey,
  isLoading,
  selectedRows,
  onSelectionChange,
  renderRow,
  bulkActions,
  onUpdate,
  truncated,
  loaded,
  total,
  pickerConfig = RENO_PROJECT_TABLE_CONFIG.pickerConfig,
}: RenoProjectGroupedTableProps) => (
  <>
    {truncated && (
      <div className="mx-6 mb-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <Warning className="h-4 w-4 shrink-0" weight="duotone" />
        <span>
          {loaded} van {total} tickets geladen. De tellers per project en onderdeel zijn
          volledig; verfijn je filters om alle tickets in de lijst te zien.
        </span>
      </div>
    )}

    {/* De gegroepeerde weergave rendert enkel accordeongroepen; zonder rijen is
        er dus niets te zien. Daarom hier een eigen lege/laad-status. */}
    {rows.length === 0 && (
      <div className="mx-6 mb-2 rounded-md border border-gray-200 bg-white px-4 py-10 text-center">
        <p className="text-neutral-primary text-sm font-medium">
          {isLoading ? 'Renovatietickets laden...' : 'Geen renovatietickets gevonden'}
        </p>
        {!isLoading && (
          <p className="text-neutral-secondary-mid mt-1 text-xs">
            Pas je filters aan of maak een nieuw ticket aan.
          </p>
        )}
      </div>
    )}

    <UniversalTable
      columnsConfig={RENO_PROJECT_TABLE_CONFIG.columnsConfig}
      pickerConfig={pickerConfig}
      sortConfig={RENO_PROJECT_TABLE_CONFIG.sortConfig}
      rows={rows}
      groupBy="projectGroupKey"
      subGroupBy="partGroupKey"
      allowMultipleGroupsOpen
      renderGroupHeader={(groupKey, groupRows) => (
        <RenoProjectGroupHeader
          group={groupsByKey.get(groupKey)}
          fallbackCount={groupRows.length}
        />
      )}
      renderSubGroupHeader={(subGroupKey, subGroupRows) => (
        <RenoPartGroupHeader
          part={partsByKey.get(subGroupKey)?.part}
          fallbackCount={subGroupRows.length}
        />
      )}
      title=""
      isLoading={isLoading}
      renderRow={renderRow}
      selectedRows={selectedRows}
      onSelectionChange={onSelectionChange}
      searchConfig={{ bulkActions }}
      onUpdate={onUpdate}
    />
  </>
);
